// src/components/CitationSearch.jsx
import React, { useEffect, useId, useState } from "react";
import { Button, Combobox, Dropdown, Input, Option } from "@fluentui/react-components";
import { SearchRegular } from "@fluentui/react-icons";
import "../../styles/citemed-theme.css";
import logo from "../../assets/citemed_logo.png";

import { apiClient } from "../../api";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastProvider";
import { SettingsDialog } from "../../components/SettingsDialog";
import Loader from "../../components/Loader";
import { ArticleList } from "./ArticleList";
import { FilterBar, DEFAULT_STATES } from "./FilterBar";
import { addToStoredMapping, getStoredCitationMapping, buildArticleIdToNumberMap } from "../../utils/documentProperties";

// Removed useStyles import

/**
 * Superscript Unicode character map for AMA style
 */
const SUPERSCRIPT_MAP = {
    '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
    '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹'
};

/**
 * Convert a number to superscript Unicode string
 * @param {number|string} num - Number to convert
 * @returns {string} - Superscript Unicode string
 */
function toSuperscript(num) {
    return num.toString().split('').map(digit => SUPERSCRIPT_MAP[digit] || digit).join('');
}

/**
 * Convert superscript Unicode string back to regular number
 * @param {string} superscript - Superscript Unicode string
 * @returns {string} - Regular number string
 */
function fromSuperscript(superscript) {
    const reverseMap = Object.fromEntries(
        Object.entries(SUPERSCRIPT_MAP).map(([k, v]) => [v, k])
    );
    return superscript.split('').map(char => reverseMap[char] || char).join('');
}

const Citation = () => {
    const dropdownId = useId("dropdown");
    const { showToast } = useToast();
    const { apiKey, selectedCitationStyle, projects, selectedProject, setAuthData } = useAuth();
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [articles, setArticles] = useState([]);
    const [selectedArticles, setSelectedArticles] = useState([]);
    const [projectSearchValue, setProjectSearchValue] = useState("");
    const [states, setStates] = useState(DEFAULT_STATES);
    const [tagIds, setTagIds] = useState([]);
    const [projectTags, setProjectTags] = useState([]);
    const [deviceIds, setDeviceIds] = useState([]);
    const [year, setYear] = useState("");
    const [devices, setDevices] = useState([]);
    const fetchAbortRef = React.useRef(null);

    // Load device filter options once authenticated
    useEffect(() => {
        if (!apiKey) return;
        const controller = new AbortController();
        apiClient
            .getFilterOptions({ signal: controller.signal })
            .then(res => setDevices(res.devices || []))
            .catch(err => {
                if (err.name !== 'AbortError') console.warn('Failed to load filter options', err);
            });
        return () => controller.abort();
    }, [apiKey]);

    // Load tags whenever the selected project changes
    useEffect(() => {
        if (!apiKey || !selectedProject?.id) {
            setProjectTags([]);
            return;
        }
        const controller = new AbortController();
        apiClient
            .getProjectTags(selectedProject.id, { signal: controller.signal })
            .then(res => setProjectTags(res.tags || []))
            .catch(err => {
                if (err.name !== 'AbortError') {
                    console.warn('Failed to load project tags', err);
                    setProjectTags([]);
                }
            });
        // Reset tag filter on project change
        setTagIds([]);
        return () => controller.abort();
    }, [apiKey, selectedProject?.id]);

    // Debounce the raw search + year inputs
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(t);
    }, [search]);
    const [debouncedYear, setDebouncedYear] = useState("");
    useEffect(() => {
        const t = setTimeout(() => setDebouncedYear(year), 300);
        return () => clearTimeout(t);
    }, [year]);

    // Reset to page 1 whenever filters, project, or debounced search change
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedProject?.id, states.join(','), tagIds.join(','), deviceIds.join(','), debouncedYear, debouncedSearch]);

    // Fetch articles when page / filters / auth / project changes
    useEffect(() => {
        if (apiKey) {
            apiClient.setApiKey(apiKey);
            fetchArticles(currentPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiKey, currentPage, selectedProject?.id, states, tagIds, deviceIds, debouncedYear, debouncedSearch]);

    // Sync projectSearchValue with selectedProject
    useEffect(() => {
        setProjectSearchValue(selectedProject?.project_name || "");
    }, [selectedProject?.project_name]);


    async function fetchArticles(page) {
        if (!selectedProject?.id) return;

        // Cancel any in-flight fetch so stale responses don't overwrite fresh ones
        if (fetchAbortRef.current) fetchAbortRef.current.abort();
        const controller = new AbortController();
        fetchAbortRef.current = controller;

        const isLoadMore = page > 1;
        if (isLoadMore) setLoadingMore(true); else setLoading(true);

        try {
            const res = await apiClient.getArticles(
                page,
                debouncedSearch,
                selectedProject.id,
                { states, tagIds, deviceIds, year: debouncedYear, signal: controller.signal }
            );
            setLastPage(res.last_page_number || 1);
            setArticles(prev => (isLoadMore ? [...prev, ...res.articles] : res.articles));
        } catch (error) {
            if (error.name === 'AbortError') return;
            showToast("Error fetching articles", error?.message, { intent: "error" });
        } finally {
            if (isLoadMore) setLoadingMore(false); else setLoading(false);
        }
    };

    function loadMore() {
        if (loading || loadingMore) return;
        if (currentPage >= lastPage) return;
        setCurrentPage(p => p + 1);
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter') {
            e.preventDefault();
            // Skip the debounce on Enter
            setDebouncedSearch(search);
        }
    };

    async function addCitationToWord() {
        try {
            setLoading(true);

            // Load stored mapping so we can dedupe by article_id and reuse
            // existing citation numbers for articles that were already cited.
            const storedMapping = await getStoredCitationMapping();
            const articleIdToNumber = buildArticleIdToNumberMap(storedMapping);
            let maxExistingNumber = 0;
            for (const n of articleIdToNumber.values()) {
                if (n > maxExistingNumber) maxExistingNumber = n;
            }

            // Request both in-text citations and bibliography
            const response = await apiClient.getCitations(
                selectedCitationStyle,
                [selectedArticles],
                "both",  // Get both in-text citations and bibliography
                "text"   // Use text format
            );

            Word.run(async (context) => {
                const selection = context.document.getSelection();
                const body = context.document.body;

                let citations = response.citations || [];
                let bibliography = response.bibliography || [];

                if (citations.length > 0) {
                    // Check whether a References section already exists (so we
                    // know to create one when adding the first new entry).
                    const searchResults = body.search("References", { matchCase: false, matchWholeWord: true });
                    context.load(searchResults, "items");
                    await context.sync();
                    const hasReferencesSection = searchResults.items.length > 0;

                    const style = selectedCitationStyle.toLowerCase();
                    const isNumberedStyle = ['vancouver', 'ieee', 'ama', 'critical-care'].includes(style);
                    const newBibEntries = [];
                    const adjustedCitations = [];
                    let nextNewNumber = maxExistingNumber + 1;

                    for (let i = 0; i < citations.length; i++) {
                        const cit = citations[i];
                        const articleId = cit.article_id;
                        let citationNum;

                        if (articleIdToNumber.has(articleId)) {
                            // Article was previously cited — reuse its number,
                            // don't add another bibliography entry.
                            citationNum = articleIdToNumber.get(articleId);
                        } else {
                            citationNum = nextNewNumber++;
                            articleIdToNumber.set(articleId, citationNum);

                            if (i < bibliography.length) {
                                const original = bibliography[i];
                                let entry;
                                if (/^\[\d+\]/.test(original)) {
                                    entry = original.replace(/^\[\d+\]/, `[${citationNum}]`);
                                } else if (/^\d+\./.test(original)) {
                                    entry = original.replace(/^\d+\./, `${citationNum}.`);
                                } else {
                                    // Author-date styles (APA, Chicago, ...) return
                                    // entries with no leading number — prepend one.
                                    entry = `${citationNum}. ${original}`;
                                }
                                newBibEntries.push(entry);
                            }
                        }

                        const updatedCitation = { ...cit };
                        if (isNumberedStyle) {
                            if (style === 'vancouver' || style === 'critical-care') {
                                updatedCitation.in_text = `(${citationNum})`;
                            } else if (style === 'ieee') {
                                updatedCitation.in_text = `[${citationNum}]`;
                            } else if (style === 'ama') {
                                updatedCitation.in_text = toSuperscript(citationNum);
                            }
                        }
                        adjustedCitations.push(updatedCitation);
                    }

                    citations = adjustedCitations;

                    // Insert in-text citations at cursor
                    let currentSelection = selection;
                    const isAMA = style === 'ama';

                    for (let i = 0; i < citations.length; i++) {
                        const cit = citations[i];
                        let citationText = cit.in_text;
                        if (isAMA) citationText = citationText + ' ';

                        currentSelection.insertText(citationText, Word.InsertLocation.replace);
                        await context.sync();

                        if (i < citations.length - 1) {
                            const range = currentSelection.getRange(Word.RangeLocation.end);
                            range.select();
                            await context.sync();
                            currentSelection = context.document.getSelection();
                            await context.sync();
                        }
                    }

                    const finalRange = currentSelection.getRange(Word.RangeLocation.end);
                    finalRange.select();
                    await context.sync();

                    // Append new bibliography entries
                    if (newBibEntries.length > 0) {
                        if (!hasReferencesSection) {
                            const endParagraph = body.paragraphs.getLast();
                            endParagraph.insertParagraph("References", Word.InsertLocation.after);
                            endParagraph.insertParagraph("", Word.InsertLocation.after);
                            endParagraph.insertParagraph("", Word.InsertLocation.after);
                        }

                        let insertPoint = body.paragraphs.getLast();
                        context.load(insertPoint, "text");
                        await context.sync();

                        // Walk back past trailing empty paragraphs so new entries
                        // attach directly after the last bibliography entry, not
                        // after a blank line.
                        while (insertPoint.text.trim() === "") {
                            const prev = insertPoint.getPreviousOrNullObject();
                            context.load(prev, "text,isNullObject");
                            await context.sync();
                            if (prev.isNullObject) break;
                            insertPoint = prev;
                        }

                        for (const entry of newBibEntries) {
                            insertPoint = insertPoint.insertParagraph(entry, Word.InsertLocation.after);
                        }
                    }
                }

                await context.sync();
                
                // Step 6: Save citations to document properties for style conversion
                if (citations.length > 0) {
                    try {
                        // Prepare citation data for saving
                        const citationsToSave = citations.map((cit, idx) => {
                            let insertedText = cit.in_text;
                            // For AMA style, add space (matching what was inserted)
                            if (selectedCitationStyle.toLowerCase() === 'ama') {
                                insertedText = insertedText + ' ';
                            }
                            return {
                                article_id: cit.article_id,
                                inserted_text: insertedText,
                                group_id: `g_${Date.now()}_${idx}_${Math.random()}`,
                                citation_number: cit.citation_number
                            };
                        });
                        
                        // Get base citations in Chicago format for better conversion support
                        let baseCitations = null;
                        try {
                            const baseResponse = await apiClient.getCitations(
                                'chicago',
                                [selectedArticles],
                                'both',
                                'text'
                            );
                            
                            if (baseResponse && baseResponse.citations) {
                                baseCitations = baseResponse.citations.map((cit, idx) => ({
                                    article_id: cit.article_id,
                                    inserted_text: cit.in_text,
                                    group_id: citationsToSave[idx]?.group_id || `g_${Date.now()}_${idx}`,
                                    citation_number: cit.citation_number
                                }));
                            }
                        } catch (baseError) {
                            // Continue without base citations
                        }

                        // Save citations to document properties
                        await addToStoredMapping(citationsToSave, selectedCitationStyle, baseCitations);
                    } catch (saveError) {
                        // Don't fail the entire operation if saving fails
                    }
                }

                setSelectedArticles([]);
                setLoading(false);

            }).catch((error) => {
                showToast("Error inserting citation", error?.message, { intent: "error" });
                setLoading(false);
            });
        } catch (error) {
            showToast("Error inserting citation", error?.message, { intent: "error" });
            setLoading(false);
        }
    };

    function handleProjectSelect(_, data) {
        setAuthData({ selectedProject: { id: data.optionValue, project_name: data.optionText } });
        setProjectSearchValue(data.optionText || "");
    }

    // Show all projects when the input still matches the selected project
    // (user hasn't started typing a new query). Otherwise filter by input.
    const isUnfiltered = !projectSearchValue ||
        projectSearchValue === selectedProject?.project_name;
    const filteredProjects = isUnfiltered
        ? projects
        : projects.filter((project) =>
            project.project_name.toLowerCase().includes(projectSearchValue.toLowerCase())
        );

    const styles = {
        container: {
            height: '100vh',
            backgroundColor: '#FAFAFF',
            fontFamily: 'Inter, sans-serif',
            display: 'flex',
            flexDirection: 'column',
        },
        header: {
            background: 'linear-gradient(180deg, #FFFFFF 0%, #F9FAFC 100%)',
            borderBottom: '1px solid var(--Gray-50)',
            padding: '8px 12px',
        },
        logoBar: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingBottom: '6px',
            borderBottom: '1px solid var(--Gray-50)',
        },
        logo: {
            height: '24px',
            width: 'auto',
        },
        brandText: {
            fontSize: '12px',
            color: 'var(--Gray-600)',
            fontWeight: 500,
        },
        content: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        },
        searchSection: {
            padding: '0 0 0 0',
        },
        projectSelect: {
            width: '100%',
            marginBottom: '6px',
        },
        searchForm: {
            width: '100%',
        },
        select: {
            width: '100%',
        },
        searchInput: {
            width: '100%',
        },
        footer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '6px 12px',
            backgroundColor: 'white',
            borderTop: '1px solid var(--Gray-50)',
            boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.03)',
            minHeight: '32px',
        },
        selectedCount: {
            fontSize: '12px',
            color: 'var(--Gray-700)',
        },
        footerActions: {
            display: 'flex',
            gap: '6px',
            alignItems: 'center',
        },
    };

    return (
        <div style={styles.container}>
            <div style={styles.content}>
                <Loader loading={loading} text="Loading articles..." />
                <div style={styles.header}>
                    <div style={styles.logoBar}>
                        <img src={logo} alt="CiteMed" style={styles.logo} />
                    </div>
                    <div style={styles.searchSection}>
                        <div style={styles.projectSelect}>
                            <Combobox
                                id={dropdownId}
                                placeholder="Search or select project..."
                                appearance="filled-lighter"
                                style={styles.select}
                                value={projectSearchValue}
                                onChange={(e) => setProjectSearchValue(e.target.value)}
                                onFocus={(e) => e.target.select()}
                                onOptionSelect={handleProjectSelect}
                                selectedOptions={selectedProject?.id ? [selectedProject.id] : []}
                                freeform
                        >
                            {filteredProjects.map((project) => (
                                <Option key={project.id} value={project.id} text={project.project_name}>
                                    {project.project_name}
                                </Option>
                            ))}
                            </Combobox>
                        </div>
                        <div id="search-form" style={styles.searchForm}>
                            <Input
                                placeholder="Search for citation..."
                                contentBefore={<SearchRegular />}
                                style={styles.searchInput}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={!selectedProject?.id}
                            />
                        </div>
                    </div>
                </div>
                <FilterBar
                    states={states}
                    tagIds={tagIds}
                    tags={projectTags}
                    deviceIds={deviceIds}
                    devices={devices}
                    year={year}
                    disabled={!selectedProject?.id}
                    onChangeStates={setStates}
                    onChangeTagIds={setTagIds}
                    onChangeDeviceIds={setDeviceIds}
                    onChangeYear={setYear}
                />
                <ArticleList
                    loading={loading}
                    loadingMore={loadingMore}
                    search={search}
                    articles={articles}
                    selectedArticles={selectedArticles}
                    setSelectedArticles={setSelectedArticles}
                    hasMore={currentPage < lastPage}
                    onLoadMore={loadMore}
                />
                <div style={styles.footer}>
                    <div style={styles.selectedCount}>
                        {selectedArticles.length > 0 ? (
                            <>
                                <span style={{ color: 'var(--Primary-600)', fontWeight: 600 }}>
                                    {selectedArticles.length}
                                </span>
                                {' citation'}{selectedArticles.length !== 1 ? 's' : ''} selected
                            </>
                        ) : (
                            'No citations selected'
                        )}
                    </div>
                    <div style={styles.footerActions}>
                        <SettingsDialog />
                        <button
                            className="btn btn-primary"
                            onClick={addCitationToWord}
                            disabled={!selectedArticles.length}
                        >
                            Insert Selected
                        </button>
                    </div>

                </div>
            </div>
        </div>

    );
};

export default Citation;
